import Web3 from 'web3';
import { ethers } from 'ethers';
import { Provider, Contract, Wallet } from 'necjs';
// import { log } from "../service/api-actions.js";
import web3Config from "../web3/web3Config.json";
import helperABI from "../web3/helperContract.json";
import contactABI from "../web3/contactContract.json";
import calenderABI from "../web3/calendarContract.json";
import userContractABI from "../web3/userContract.json";
import hostContractABI from "../web3/hostContract.json";
import db from '../db/db-service.js';
import { reportError, toErrorString } from '../utils/errorLogger.js';
import { resolveTier, Tier, signWithUserWallet, submitSponsored } from './txSigner.js';
const web3 = new Web3(web3Config?.CHAIN_DETAILS?.rpcUrls[0]);

const contractDetails = {
    "calendar": {
        contractId: web3Config.CALENDAR_CONTRACT,
        contractABI: calenderABI.contract,
    },
    "contact": {
        contractId: web3Config.CONTACT_CONTRACT,
        contractABI: contactABI.contract,
    },
    "helper": {
        contractId: web3Config.HELPER,
        contractABI: helperABI.contract,
    },
    "hostContract": {
        contractId: web3Config.CONTRACT,
        contractABI: hostContractABI.contract,
    },
    "userContract": {
        contractId: web3Config.USER_REGISTRY_CONTRACT,
        contractABI: userContractABI.contract,
    }
}


// LEGACY relayer path — signs with the client-side key (web3Config.NECJSPK). RETAINED ONLY as a
// temporary fallback for sponsored-tier ops that don't yet have a backend endpoint. NECJSPK is
// intentionally blank in committed config; a populated key is the deprecated shortcut we're migrating
// away from (server-sponsored writes + user-wallet signing), not the target design.
const legacyRelayerSend = async (contractId, contractABI, functionName, functionParams) => {
    if (!web3Config.NECJSPK) {
        throw new Error(`"${functionName}" has no server-sponsored endpoint yet and no client relayer is configured. Add it to SPONSORED_OPS + a /mail/chain/* endpoint.`);
    }
    const wallet = await Wallet.connect(web3Config.NECJSPK, web3Config.CHAIN_DETAILS.rpcUrls[0]);
    const contract = new Contract(contractId, contractABI, wallet.provider, wallet.signer);
    const method = contract.methods[functionName](...functionParams);
    const gas = await method.estimateGas({ from: wallet.address }).catch((error) => { throw error; });
    if (!gas) return null;
    const nonce = await wallet.provider.getTransactionCount(wallet.address);
    const txHash = await method.send({ from: wallet.address, gas, nonce });
    console.log('Transaction successful (legacy relayer):', txHash);
    return txHash;
};

// Common transaction method for all the write operations. Routes by payer tier (see txSigner.js):
//   FREE      → the user's own NCOG wallet signs + pays (ncog_sendTransaction).
//   SPONSORED → organization/Securus: the server relayer signs + funds (POST /mail/chain/*),
//               falling back to the legacy relayer only for not-yet-migrated ops.
export const transactionAction = async (_contract, functionName, functionParams, _senderAccount, contractType, extraContractId = undefined) => {
    try {
        let contractId = undefined;
        let contractABI = undefined;
        if (extraContractId) {
            contractId = extraContractId;
            contractABI = contractType;
        } else {
            contractId = contractDetails[contractType]?.contractId;
            contractABI = contractDetails[contractType]?.contractABI;
        }

        const tier = await resolveTier();

        // FREE tier — the user's own NCOG wallet signs, confirms, and pays gas. No key in the client.
        if (tier === Tier.FREE) {
            let hash = null;
            let walletError = null;
            try {
                hash = await signWithUserWallet({ contractId, contractABI, functionName, functionParams, from: _senderAccount });
            } catch (e) {
                walletError = e;
            }
            if (hash) return hash;

            // The wallet either errored or gave back no usable hash (an older extension
            // build reports both cases identically). DEV-ONLY safety net so testing can
            // continue: NEVER ship with NECJSPK set — it puts the relayer's private key
            // in the browser bundle. Without it we surface the failure, as we should.
            if (web3Config.NECJSPK) {
                const why = walletError ? String(walletError.message || walletError).slice(0, 140) : 'no transaction hash returned';
                console.warn(`[chain] wallet could not sign "${functionName}" (${why}) — falling back to the DEV relayer key. Do NOT ship this config.`);
                return await legacyRelayerSend(contractId, contractABI, functionName, functionParams);
            }
            if (walletError) throw walletError;
            return null;
        }

        // SPONSORED tier — the server relayer signs + funds via the backend.
        const sponsored = await submitSponsored({ functionName, functionParams });
        if (sponsored.migrated) return sponsored.txHash;

        // Sponsored op not migrated to a backend endpoint yet → temporary legacy relayer fallback.
        console.warn(`[chain] "${functionName}" has no sponsored endpoint yet; using legacy relayer fallback. Migrate it to /mail/chain/*.`);
        return await legacyRelayerSend(contractId, contractABI, functionName, functionParams);
    } catch (error) {
        console.error("Transaction failed:", error);
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '-',
            message: `FileName: chain-Helper.js, Fun: transactionAction, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            userName: userName || '',
        });
        return null;
    }
}

// // Common transaction method for all the write operation
// export const transactionAction = async (contract, functionName, functionParams, senderAccount, contractType, extraContractId = undefined) => {
//     try {
//         // const organizationData = await db.table('organizationData').toArray();
//         // if (organizationData?.length > 0 && organizationData[0]?.uuid) {
//             // Use ethers.js for organizationId case
//             const provider = new ethers.JsonRpcProvider(web3Config.CHAIN_DETAILS.rpcUrls[0]);
//             const senderWallet = new ethers.Wallet(web3Config.PRIVATE_KEY, provider);

//             let contractId = undefined;
//             let contractABI = undefined;
//             if (extraContractId) {
//                 contractId = extraContractId;
//                 contractABI = contractType;
//             } else {
//                 contractId = contractDetails[contractType].contractId;
//                 contractABI = contractDetails[contractType].contractABI;
//             }

//             const contractWithWallet = new ethers.Contract(contractId, contractABI, senderWallet);
//             try {
//                 const tx = await contractWithWallet[functionName](...functionParams);
//                 const receipt = await tx.wait();
//                 console.log('Transaction successful:', receipt?.hash);
//                 return receipt?.hash;
//             } catch (error) {
//                 console.error("Transaction failed:", error);
//                 return null;
//             }
//         // } else {
//         //     const { account, contractMeta } = await connectMetaMask(contractType);
//         //     if (!account) {
//         //         return null;
//         //     }
//         //     let gasLimit = undefined;
//         //     // get estimated gas for the transaction
//         //     await contractMeta.methods[functionName](...functionParams).estimateGas({ from: account }).then(function (gasAmount) {
//         //         gasLimit = gasAmount;
//         //     })
//         //         .catch(function (error) {
//         //             console.log("gasLimit error", error)
//         //         });
//         //     // Get current gas price from the network
//         //     const gasPrice = await web3.eth.getGasPrice();

//         //     // Send the transaction
//         //     const transaction = await contractMeta.methods[functionName](...functionParams).send({ from: account, gas: gasLimit, gasPrice: gasPrice });

//         //     // Get transaction receipt
//         //     const receipt = await web3.eth.getTransactionReceipt(transaction.transactionHash);

//         //     // Extract transaction hash
//         //     const txHash = receipt.transactionHash;
//         //     await log(txHash, 'transaction');

//         //     console.log("txHash", txHash)
//         //     return txHash;
//         // }
//     } catch (error) {
//         console.error("error", error)
//         await log(JSON.stringify(error));
//         return null;
//     }
// }

// const connectMetaMask = async (contractType) => {
//     if (!window.ethereum) {
//         console.error("MetaMask not detected");
//         return null;
//     }

//     try {
//         const accounts = await window.ethereum.request({ method: 'eth_accounts' });
//         if (!accounts.length) {
//             console.error("No accounts found. Please connect to NCOG.");
//             return null;
//         }

//         const chainId = parseInt(web3Config.CHAIN_ID);
//         const currentChainId = parseInt(window.ethereum.networkVersion);

//         if (currentChainId !== chainId) {
//             await switchOrAddChain(chainId);
//         }

//         const { contractId, contractABI } = contractDetails[contractType] || {};
//         if (!contractId || !contractABI) {
//             console.error("Invalid contract type or missing contract details");
//             return null;
//         }

//         const web3 = new Web3(window.ethereum);
//         const contractMeta = new web3.eth.Contract(contractABI, contractId);

//         return { account: accounts[0], contractMeta };
//     } catch (error) {
//         console.error("Error connecting to NCOG:", error);
//         return null;
//     }
// };

const switchOrAddChain = async (chainId) => {
    try {
        await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: web3.utils.toHex(chainId) }]
        });
    } catch (error) {
        if (error.code === 4902) {
            await addEthereumChain(chainId);
        } else {
            throw error;
        }
    }
};

const addEthereumChain = async (chainId) => {
    try {
        await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
                chainId: web3.utils.toHex(chainId),
                chainName: web3Config.CHAIN_DETAILS.chainName,
                nativeCurrency: web3Config.CHAIN_DETAILS.nativeCurrency,
                rpcUrls: web3Config.CHAIN_DETAILS.rpcUrls,
                blockExplorerUrls: web3Config.CHAIN_DETAILS.blockExplorerUrls,
            }]
        });
    } catch (error) {
        const userName = localStorage.getItem('UserName');
        const errorStr = toErrorString(error || error.message);
        reportError({
            endpoint: '-',
            message: `FileName: chain-Helper.js, Fun: addEthereumChain, ${String(error.message || 'Unhandled error')}`,
            error: errorStr,
            level: 'warn',
            userName: userName || '',
        });
        console.error("Failed to add new Ethereum chain:", error);
        throw error;
    }
};

export const getEstimateGasFee = async (contract, functionName, functionParams, senderAccount) => {
    const gasLimit = await contract.methods[functionName](...functionParams).estimateGas({ from: senderAccount });
    return gasLimit;
}